/**
 * utils/contactMatch.js
 * Pure, tested heuristics for "might these two contact entries be the same real firm?" — the
 * app NEVER decides this on its own (same rule as `persons.js`'s `detectPersonCollision`): this
 * module only ever suggests, ranked with a stated reason; a human always confirms the actual
 * `businessId` link via `contacts.js`'s `updateContact`.
 *
 * Missing a real match is worse than surfacing a wrong one — a wrong suggestion costs a glance,
 * a missed one leaves a firm silently split with its combined total quietly wrong forever. Cast
 * wide: every heuristic below is deliberately loose. Signals, strongest first:
 *   1. a shared phone number, ignoring formatting
 *   2. a name match after stripping a trailing " - CITY"-style suffix
 *   3. name similarity tolerant of spacing/spelling drift ("BALI MAS MOTOR" vs "BALIMAS MOTOR")
 *   4. name containment — a Pusat entry is often the owner's name with the shop in brackets,
 *      e.g. "HARRY SOEN (ABADI MOTOR)" contains "ABADI MOTOR"
 *   5. address EXACT match (the WHOLE normalized address, never a prefix or a fuzzy ratio — see
 *      that check's own comment for why fuzzy was tried and rejected) — ranked last and
 *      noisiest on purpose
 *
 * Kept decoupled from the live `Contact` shape (`contactMatchScore` takes plain
 * `{name, phone, address}` descriptors) specifically so the eventual bulk import matcher (663
 * rows) can reuse the same scorer against parsed rows, not just live contacts.
 *
 * **Contacts xlsx import, batch 3b (`contactImportGroup.js`) is that bulk matcher.** It calls
 * `contactMatchScore` ~220,000 times over 663 rows, reusing the same descriptor object as an
 * argument up to ~660 times each — so the five string transforms below (`normName`/
 * `stripTrailingSuffix`/`condense`/`looseName`/`normalizePhone`) are memoized per descriptor
 * OBJECT in a module-level `WeakMap` (`derivedFor`/`computeDerived`), rather than recomputed on
 * every call. This is a pure speed-up: it changes nothing about what any call returns (every
 * existing test below still passes unmodified), and a descriptor built fresh per call — as
 * `suggestBusinessMatches` already does today, one per candidate — costs nothing extra either,
 * since its cache entry is simply never reused and is garbage-collected normally.
 */

function normName(s) {
  return String(s || "").trim().toUpperCase().replace(/\s+/g, " ");
}

/** Strip a trailing " - XXX" / " – XXX" short suffix (a city/branch tag), e.g.
 *  "ABADI MOTOR - BKT" -> "ABADI MOTOR". Only strips a SHORT trailing token (<=8 chars) after a
 *  dash, so a genuinely two-part company name with a long second half is left alone. */
function stripTrailingSuffix(name) {
  return name.replace(/\s*[-–]\s*[A-Z0-9.]{1,8}$/, "").trim();
}

/** Strip everything but letters/digits — catches pure spacing drift ("BALI MAS MOTOR" vs
 *  "BALIMAS MOTOR" become identical) and gives a clean base for the edit-distance check below. */
function condense(name) {
  return name.replace(/[^A-Z0-9]/g, "");
}

/** Names with punctuation reduced to spaces (parens, dashes, commas, ...) and collapsed — used
 *  for the containment check so "HARRY SOEN (ABADI MOTOR)" reads as "HARRY SOEN ABADI MOTOR"
 *  and a plain substring test finds "ABADI MOTOR" inside it. */
function looseName(name) {
  return name.replace(/[^A-Z0-9\s]/g, " ").replace(/\s+/g, " ").trim();
}

/** Digits only — "(031) 555-1234" and "031-5551234" normalize to the same string. Empty input
 *  (no phone on file) normalizes to "", which never matches anything (see callers: both sides
 *  must be non-empty to count as a signal). */
export function normalizePhone(s) {
  return String(s || "").replace(/\D+/g, "");
}

/** Plain Levenshtein edit distance — small strings only (contact names), no need for a
 *  space-optimized variant. */
function editDistance(a, b) {
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = new Array(n + 1);
  let curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
}

/** 1 = identical, 0 = completely different. */
function similarityRatio(a, b) {
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  return 1 - editDistance(a, b) / maxLen;
}

// Weights double as the ranking tiers ("strongest first" from the file header) — a candidate's
// score is its STRONGEST matched signal (plus a small +1-per-extra-signal nudge so two
// corroborating weaker signals can edge out a lone one of the same tier), never a plain sum —
// see `contactMatchScore` below.
const WEIGHT = {
  PHONE: 100,
  NAME_EXACT: 95,
  NAME_STRIP_SUFFIX: 85,
  NAME_SIMILAR: 65,
  NAME_CONTAINS: 55,
  ADDRESS_MATCH: 15,
};

const SIMILARITY_THRESHOLD = 0.82; // condensed-name edit-distance ratio for "spelling drift"
const MIN_CONTAINMENT_LEN = 4; // shorter name must be at least this long to count as "contained"

// A shared STREET NAME is not evidence — measured on a real 663-shop file, "JL A YANI " (the
// first 10 chars) alone was shared by 43 different shops, "JL JEND SU" by 23, "JL BANDANG" by
// 16, 21 distinct 10-char prefixes by 5+ shops each — a plain shared-prefix rule chained almost
// the whole file into a handful of enormous, meaningless groups. Only 18 pairs in that same file
// shared their FULL normalized address, and those were the real matches this signal exists to
// catch. So this requires the WHOLE address to match — EXACTLY, never a prefix.
//
// A ratio-based "near-exact" tier (e.g. edit-distance ratio >= 0.92 on the whole string) was
// tried and rejected after real-file testing: two real, DIFFERENT shops on the same street
// ("JL TUANKU TAMBUSAI NO 6, PEKANBARU" vs "...NO 34, PEKANBARU") still cleared a 0.92 ratio,
// because a differing HOUSE NUMBER is only a couple characters inside a long shared street+city
// string — the same false-positive failure as the prefix rule, just reached a different way. A
// ratio (or even a small absolute edit-distance cap) can't tell "a typo" from "a different house
// number" when both are a 1-3 character difference in a 30-60 character string. Exact match only
// avoids that failure mode entirely, at the cost of missing a genuine typo'd duplicate — an
// acceptable trade per this file's own "missing a real match is worse... but only when the
// alternative doesn't actively mislead" balance: a false NEGATIVE here just means one real
// duplicate isn't suggested (recoverable — `suggestBusinessMatches`'s other signals, or a human
// noticing, still catch it); the false POSITIVE this ratio produced actively chained unrelated
// shops together, which is strictly worse.

/** Every per-descriptor derived form `contactMatchScore` needs, computed once. Cached below
 *  (`derivedFor`) keyed on the descriptor OBJECT itself — see that function's own comment for
 *  why: a bulk N-way matcher (`contactImportGroup.js`, batch 3b of the contacts import) calls
 *  this function ~220,000 times over just 663 real rows, reusing the SAME descriptor object as
 *  an argument up to ~660 times each; recomputing five string transforms from scratch on every
 *  one of those calls was the measured majority of that bulk matcher's own wall-clock cost. */
function computeDerived(d) {
  const nameNorm = normName(d?.name);
  return {
    phoneNorm: normalizePhone(d?.phone),
    addrNorm: normName(d?.address),
    nameNorm,
    stripped: stripTrailingSuffix(nameNorm),
    condensed: condense(nameNorm),
    loose: looseName(nameNorm),
  };
}

/** WeakMap so a descriptor object that's only ever used once (e.g. `suggestBusinessMatches`'s
 *  own fresh `descriptorFor(c)` on every call) costs nothing extra and is GC'd normally — this
 *  is purely an internal speed-up, never a change to what `contactMatchScore` returns for any
 *  input. Non-object input (defensive only — every real caller passes a descriptor object) skips
 *  the cache rather than risking a `WeakMap.set` TypeError on a non-object key. */
const derivedCache = new WeakMap();
function derivedFor(d) {
  if (!d || typeof d !== "object") return computeDerived(d);
  let cached = derivedCache.get(d);
  if (!cached) {
    cached = computeDerived(d);
    derivedCache.set(d, cached);
  }
  return cached;
}

/**
 * Score a single pair of "is this the same firm?" descriptors — `{name, phone, address}` each
 * (any may be blank/absent). Returns `{score, reasons}`; `score === 0` (`reasons: []`) means no
 * signal fired at all. Pure, deterministic, no knowledge of the live Contact/business shape.
 */
export function contactMatchScore(a, b) {
  const reasons = [];
  let weight = 0;
  const bump = (w, reason) => { reasons.push(reason); if (w > weight) weight = w; };

  const da = derivedFor(a);
  const db = derivedFor(b);

  if (da.phoneNorm && db.phoneNorm && da.phoneNorm === db.phoneNorm) {
    bump(WEIGHT.PHONE, "Nomor telepon sama");
  }

  if (da.nameNorm && db.nameNorm) {
    if (da.nameNorm === db.nameNorm) {
      bump(WEIGHT.NAME_EXACT, "Nama sama persis");
    } else {
      if (da.stripped && db.stripped && da.stripped === db.stripped) {
        bump(WEIGHT.NAME_STRIP_SUFFIX, "Nama sama setelah menghapus akhiran kota");
      } else if (da.condensed && db.condensed) {
        if (da.condensed === db.condensed) {
          bump(WEIGHT.NAME_SIMILAR, "Nama mirip (hanya beda spasi)");
        } else if (similarityRatio(da.condensed, db.condensed) >= SIMILARITY_THRESHOLD) {
          bump(WEIGHT.NAME_SIMILAR, "Nama mirip");
        }
      }

      if (da.loose && db.loose && da.loose !== db.loose) {
        const shorter = da.loose.length <= db.loose.length ? da.loose : db.loose;
        const longer = da.loose.length <= db.loose.length ? db.loose : da.loose;
        if (shorter.length >= MIN_CONTAINMENT_LEN && longer.includes(shorter)) {
          bump(WEIGHT.NAME_CONTAINS, "Nama salah satu mengandung nama lainnya");
        }
      }
    }
  }

  if (da.addrNorm && db.addrNorm && da.addrNorm === db.addrNorm) {
    bump(WEIGHT.ADDRESS_MATCH, "Alamat sama persis");
  }

  if (reasons.length === 0) return { score: 0, reasons: [] };
  return { score: weight + (reasons.length - 1), reasons };
}

/** Extract the plain `{name, phone, address}` descriptor a live Contact scores against —
 *  `address` prefers the marked-default address, else the first one, else "". */
function descriptorFor(contact) {
  const addr = (contact?.addresses || []).find((a) => a.isDefault) || (contact?.addresses || [])[0];
  return { name: contact?.name, phone: contact?.phone, address: addr ? addr.text : "" };
}

/**
 * Rank every OTHER contact in `candidates` as a possible same-firm match for `contact`.
 * Excludes `contact` itself, any `internal` stand-in (never a real firm), and anyone already
 * sharing `contact`'s own business (already linked — nothing to suggest). Does NOT exclude a
 * deactivated contact, or one already linked to a DIFFERENT business — both are still real,
 * possibly-the-same-firm entries a human should get to see and judge.
 *
 * Returns `[{contact, score, reasons}]` sorted score-descending (ties broken by name), score>0
 * only — zero signal means no suggestion, never a guessed one. `limit` caps the result length
 * (default 5); pass `null` for no cap.
 */
export function suggestBusinessMatches(contact, candidates = [], { limit = 5 } = {}) {
  if (!contact) return [];
  const target = descriptorFor(contact);
  const results = [];
  for (const c of candidates) {
    if (!c || c.id === contact.id || c.internal) continue;
    if (contact.businessId && c.businessId && c.businessId === contact.businessId) continue;
    const { score, reasons } = contactMatchScore(target, descriptorFor(c));
    if (score > 0) results.push({ contact: c, score, reasons });
  }
  results.sort((x, y) => y.score - x.score || String(x.contact.name).localeCompare(String(y.contact.name)));
  return typeof limit === "number" ? results.slice(0, limit) : results;
}
